import { Sparkles, Clock } from 'lucide-react';
import type { FlashcardCardInsights } from '../../services/flashcardService';
import type { Rating } from '../../lib/spacedRepetition/sm2';

interface FlashcardCardInsightsPanelProps {
  insights: FlashcardCardInsights | null;
  loading?: boolean;
  className?: string;
  /** Student study view vs tutor admin (wording + alignment). */
  variant?: 'student' | 'admin';
}

const RATING_LABELS: Record<Rating, { label: string; color: string }> = {
  again: { label: 'Again', color: 'text-red-600' },
  hard: { label: 'Hard', color: 'text-orange-600' },
  good: { label: 'Good', color: 'text-green-600' },
  easy: { label: 'Easy', color: 'text-blue-600' },
};

const FlashcardCardInsightsPanel = ({
  insights,
  loading,
  className = '',
  variant = 'student',
}: FlashcardCardInsightsPanelProps) => {
  const isAdmin = variant === 'admin';
  if (loading && !insights) {
    return (
      <div className={`mt-6 p-4 rounded-2xl bg-white/70 border border-gray-100 ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
        <div className="h-3 bg-gray-100 rounded w-1/2 mx-auto" />
      </div>
    );
  }

  if (!insights) return null;

  const hasHistory = insights.reviewCount > 0;
  const ratingParts = (['again', 'hard', 'good', 'easy'] as Rating[])
    .filter((r) => insights.ratingCounts[r] > 0)
    .map((r) => (
      <span key={r} className={RATING_LABELS[r].color}>
        {RATING_LABELS[r].label} {insights.ratingCounts[r]}×
      </span>
    ));

  return (
    <div
      className={`p-4 sm:p-5 rounded-2xl bg-gray-50 border border-gray-100 space-y-2 ${
        isAdmin ? 'text-left' : 'mt-6 text-center bg-white/80 backdrop-blur border-indigo-100/80 shadow-sm'
      } ${className}`}
    >
      {insights.isFirstTime ? (
        <p
          className={`text-sm font-bold text-indigo-700 flex items-center gap-1.5 ${
            isAdmin ? '' : 'justify-center'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-500" />
          {isAdmin ? 'Not studied yet' : 'Seeing this card for the first time!'}
        </p>
      ) : (
        <p className="text-sm font-bold text-gray-800">
          {isAdmin ? 'Studied' : "You've studied this card"} {insights.reviewCount} time
          {insights.reviewCount !== 1 ? 's' : ''}
          {insights.lapses > 0 && (
            <span className="text-red-600 font-semibold"> · {insights.lapses} lapse{insights.lapses !== 1 ? 's' : ''}</span>
          )}
        </p>
      )}

      {insights.lastSeenLabel && hasHistory && (
        <p
          className={`text-xs text-gray-600 flex items-center gap-1 ${
            isAdmin ? '' : 'justify-center'
          }`}
        >
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          Last seen {insights.lastSeenLabel}
        </p>
      )}

      {ratingParts.length > 0 && (
        <p
          className={`text-xs text-gray-500 flex flex-wrap gap-x-2 gap-y-1 ${
            isAdmin ? 'items-center' : 'items-center justify-center'
          }`}
        >
          {ratingParts.map((part, i) => (
            <span key={i} className="inline-flex items-center gap-x-2">
              {i > 0 && <span className="text-gray-300">·</span>}
              {part}
            </span>
          ))}
        </p>
      )}

    </div>
  );
};

export default FlashcardCardInsightsPanel;
